import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { chatPersistenceService } from "@/services/chatPersistenceService";
import {
  ConversationHookResult,
  ConversationData,
  processConversationMessages,
  createEnrichedConversation,
} from "@/utils/conversationUtils";

const useFetchConversation = (
  conversationId: string | null,
  agentId?: string
): ConversationHookResult => {
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [initialMessages, setInitialMessages] = useState<any[]>([]);
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(null);

  const fetchConversation = async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: conversationData, error: fetchError } = await supabase
        .from("conversations")
        .select(
          "*, messages(id, sender_type, content, created_at, metadata, openai_message_id), agent:agent_id(*)"
        )
        .eq("id", id)
        .order("created_at", { referencedTable: "messages", ascending: true })
        .single();

      if (fetchError) {
        setError(fetchError.message);
        setIsLoading(false);
        return null;
      }

      if (!conversationData) {
        setError("Conversation not found");
        setIsLoading(false);
        return null;
      }

      // Process messages using shared utility
      const { messages, previousResponseId: responseId } = processConversationMessages(
        conversationData.messages
      );

      // Create enriched conversation using shared utility
      const enrichedConversation = createEnrichedConversation(conversationData, responseId);

      setConversation(enrichedConversation);
      setInitialMessages(messages);
      setPreviousResponseId(responseId);
      setIsLoading(false);
      return enrichedConversation;
    } catch (error) {
      console.error("Error fetching conversation:", error);
      setError(error instanceof Error ? error.message : "Unknown error");
      setIsLoading(false);
      return null;
    }
  };

  useEffect(() => {
    const channel = supabase.channel("conversation-tracking");
    channel
      .on(
        "broadcast",
        { event: "conversation_closed" },
        (event) => {
          console.log("Conversation closed:", event);
          fetchConversation(event.payload.conversation_id);
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchConversation]);

  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId);
    } else if (agentId) {
      const getAgentPublicId = async () => {
        try {
          const { data: agent, error: agentError } = await supabase
            .from("agents")
            .select("public_id")
            .eq("id", agentId)
            .single();

          if (agentError || !agent?.public_id) {
            return;
          }

          const storedConversationId = chatPersistenceService.getStoredConversationId(agent.public_id);
          if (storedConversationId) {
            await fetchConversation(storedConversationId);
          } else {
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error fetching agent or stored conversation:", error);
          setIsLoading(false);
        }
      };

      getAgentPublicId();
    } else {
      setIsLoading(false);
    }
  }, [conversationId, agentId]);

  return {
    conversation,
    isLoading,
    error,
    conversationId: conversation?.id || conversationId,
    initialMessages,
    previousResponseId,
  };
};

export default useFetchConversation;
